/* ⚠️ Charl, 21 Sep 2026: "sometimes we do need to run it longer, but I want the ability to
   choose this and also be able to choose the length ... and for you to learn what we need."

   A CHOICE is one fact: on this shape of pass, we ran it at this length. It is recorded
   when he sets a run length on the cutting list, and when the floor records a manual pass
   (which is the stronger signal - it is what actually happened at the machine).

   Nothing here ever changes a plan by itself. It finds a matching choice so the screen can
   OFFER it - "you ran this at 6000 on 21 Sep, use it?" - and he decides. That is what he
   asked for: "I want the ability to choose."

   The situation is the girths, the part lengths and the coil width - the things that decide
   whether a run length makes sense. Not the order, not the customer, not the colour. */

/* ⚠️ Charl, 21 Sep: "please make sure the talk boxes give us an error code so that we know
   what we have done wrong or what it doesn't want to allow and why."

   Every refusal carries a short code. The code is what he reads out to me; the sentence
   after it is for the floor. Codes are never reused for a different meaning - add a new
   one rather than changing what an old one means.

     RUN-01  nothing in the box that reads as a length
     RUN-02  strips given, but no run length to go with them
     RUN-03  longer than the 6400mm fold length
     RUN-04  a piece is longer than the run it is asked to come off
     RUN-05  that run length fits nothing on this job (the nearest that does is named)
     RUN-06  turning: a piece is longer than the coil is wide
     RUN-07  nothing at all could be planned onto this coil
     RUN-08  the box is empty
     RUN-09  that length does not work ALONG the coil, but it does ACROSS it - tick the box */
const CODES = {
  "RUN-01": "I could not find a length in that",
  "RUN-02": "I got the strips but not the run length - say how long the run is",
  "RUN-03": "A run cannot be longer than 6400mm",
  "RUN-04": "A piece does not fit in that run",
  "RUN-05": "That run length fits nothing on this job",
  "RUN-06": "A piece is longer than the coil is wide",
  "RUN-07": "Nothing could be planned onto this coil",
  "RUN-08": "Nothing to read",
  "RUN-09": "That length works across the coil, not along it - tick 'Run these across the coil'",
};
/* what the screen shows: "RUN-03 - A run cannot be longer than 6400mm" */
function said(code, detail) { return code + " - " + (detail || CODES[code] || "Refused"); }

function nz(v) { return Math.round(Number(v) || 0); }

/* What a coil pass IS, for matching: its girths and part lengths, and the coil it runs on. */
function situationOf(pass, coilWidth) {
  const girths = [], lengths = [];
  (pass && pass.strips || []).forEach(s => {
    girths.push(nz(s.girth));
    (s.cuts || []).forEach(c => lengths.push(nz(c.length)));
  });
  return {
    coilWidth: nz(coilWidth || (pass && pass.coilWidth)),
    girths: [...new Set(girths)].sort((a, b) => a - b),
    lengths: [...new Set(lengths)].sort((a, b) => a - b),
  };
}

const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/* A match is the SAME situation: same coil width, same girths, same part lengths. Deliberately
   strict - a length that made sense for two 250s at 3m is not evidence about anything else,
   and a loose match is how a learned preference turns into a plan nobody asked for. */
function matchChoice(pass, coilWidth, choices) {
  const now = situationOf(pass, coilWidth);
  if (!now.girths.length || !now.lengths.length) return null;
  const hits = (choices || []).filter(c =>
    c && c.on !== false && nz(c.coilWidth) === now.coilWidth
    && same((c.girths || []).map(nz).sort((a, b) => a - b), now.girths)
    && same((c.lengths || []).map(nz).sort((a, b) => a - b), now.lengths));
  if (!hits.length) return null;
  /* the most recent one wins - the floor's latest word on it */
  return hits.slice().sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")))[0];
}

/* The words the screen shows. Plain, and it says where it came from. */
function choiceWords(c) {
  if (!c) return "";
  const when = c.at ? new Date(c.at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" }) : "";
  const who = c.by ? " by " + c.by : "";
  return "Run at " + nz(c.runLen) + "mm — "
    + (c.source === "manual" ? "what the floor ran" : "your choice")
    + (when ? " on " + when : "") + who;
}

/* ⚠️ Charl, 21 Sep: "I also need all these functions in run a manual pass."
   Turns what he types or says into a run length and the cuts, so nobody types "465x2,
   240x1" by hand. Deliberately plain rules, no AI in the middle: the screen shows what it
   understood and he corrects it BEFORE anything is recorded. What it cannot read, it says
   it cannot read - it never invents a number.

     "6m, two 250s at 3m"        -> run 6000, 2 x 250 girth at 3000
     "run at 6000, 250x2 at 3m"  -> run 6000, 2 x 250 girth at 3000
     "465x2, 240x1"              -> those girths at the run length                        */
const WORDNUM = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };

function toMm(v, unit) {
  const n = parseFloat(v);
  if (!(n > 0)) return 0;
  if (unit === "mm") return Math.round(n);
  if (unit) return Math.round(n * 1000);            // m / metre / meters
  return Math.round(n <= 7 ? n * 1000 : n);         // bare 6 is 6m, bare 6000 is mm
}

function parseManualWords(text) {
  /* "465x2" has no spaces round the x, so give every separator room - without this the
     x is not a word on its own and the clause reads as one number. */
  let t = String(text || "").toLowerCase().replace(/[×*]/g, " x ")
    .replace(/(\d)\s*x\s*(\d)/g, "$1 x $2").replace(/\band\b/g, " , ")
    .replace(/\s+/g, " ").trim();
  if (!t) return { ok: false, code: "RUN-08", why: said("RUN-08") };

  /* THE RUN: "run this pass at 6m", "run at 6000", or a length said first ("6m, two 250s
     at 3m"). Read off the front, so it cannot be mistaken for a girth later. */
  let runLen = 0, cutFrom = 0;
  let m = t.match(/\brun\b[a-z ]{0,20}?(\d+(?:\.\d+)?)\s*(mm|m|metres?|meters?)?\b/);
  if (m) { runLen = toMm(m[1], m[2]); cutFrom = m.index + m[0].length; }
  else {
    /* a bare "6000" said first is a run length too - but "465 x 2" is a strip, not a run */
    const lead = t.match(/^(\d+(?:\.\d+)?)\s*(mm|m|metres?|meters?)?\b(?!\s*x)/);
    if (lead && (lead[2] || parseFloat(lead[1]) >= 1000)) {
      runLen = toMm(lead[1], lead[2]); cutFrom = lead[0].length;
    }
  }

  /* THE CUTS, one clause at a time - "two 250s at 3m" / "250 x 2 at 3m" / "465 x 2" */
  const cuts = [];
  let sawClause = false;
  t.slice(cutFrom).split(",").forEach(part => {
    const clause = part.trim();
    if (!clause) return;
    const nums = [...clause.matchAll(/(\d+(?:\.\d+)?)\s*(mm|m|metres?|meters?)?/g)]
      .map(x => ({ n: parseFloat(x[1]), unit: x[2] || "" }));
    let count = 0;
    const w = clause.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten)\b/);
    if (w) count = WORDNUM[w[1]];
    const hasAt = /\b(at|@)\b/.test(clause);
    const hasX = /\bx\b/.test(clause);
    if (!nums.length) return;
    sawClause = true;
    let girth = 0, length = 0;
    if (count && hasAt && nums.length >= 2) {            // two 250s at 3m
      girth = nums[0].n; length = toMm(nums[1].n, nums[1].unit);
    } else if (hasX && hasAt && nums.length >= 3) {      // 250 x 2 at 3m
      girth = nums[0].n; count = nums[1].n; length = toMm(nums[2].n, nums[2].unit);
    } else if (hasX && nums.length >= 2) {               // 465 x 2  (the old shorthand)
      girth = nums[0].n; count = nums[1].n; length = 0;
    } else if (hasAt && nums.length >= 2) {              // 250 at 3m
      girth = nums[0].n; count = count || 1; length = toMm(nums[1].n, nums[1].unit);
    } else { return; }
    girth = Math.round(girth);
    count = Math.max(1, Math.round(count || 1));
    if (girth > 0) cuts.push({ girth, length: Math.round(length), count });
  });

  if (!runLen && cuts.length) runLen = cuts.reduce((t2, c) => Math.max(t2, c.length || 0), 0);
  if (!runLen) {
    return { ok: false, why: sawClause
      ? said("RUN-02") : said("RUN-01"), code: sawClause ? "RUN-02" : "RUN-01" };
  }
  if (runLen > 6400) return { ok: false, code: "RUN-03", why: said("RUN-03") };
  cuts.forEach(c => { if (!c.length) c.length = runLen; });
  const tooLong = cuts.find(c => c.length > runLen);
  if (tooLong) return { ok: false, code: "RUN-04",
    why: said("RUN-04", "A " + tooLong.length + "mm piece does not fit in a " + runLen + "mm run") };
  return { ok: true, runLen, cuts,
    understood: "Run " + runLen + "mm" + (cuts.length ? " — " + cuts.map(c =>
      c.count + " x " + c.girth + "mm girth at " + c.length + "mm").join(", ") : "") };
}

export { situationOf, matchChoice, choiceWords, parseManualWords, CODES, said };

